import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGet, axiosPost, axiosPut } from '../api/axios';

export interface ProviderTypeConfig {
  id?: number;
  provider: string;
  provider_type: string;
  content_html?: string | null;
  tile_name?: string | null;
  tile_description?: string | null;
  tile_image_url?: string | null;
  tile_image_file_data?: string | null;
  tile_tags?: string[] | null;
  online_access_url?: string | null;
}

export interface ExternalProviderPlan {
  code: string;
  display_name?: string;
  id?: number;
  name: string;
  provider: string;
  provider_types: string[];
  recuro_metatag3: string;
  /** Computed on the backend by joining external_provider_type_configs on (provider, provider_type). Read-only. */
  readonly type_configs?: ProviderTypeConfig[];
}

export const emptyProviderTypeConfig = (provider: string, provider_type: string): ProviderTypeConfig => ({
  provider,
  provider_type,
  content_html: '',
  tile_name: '',
  tile_description: '',
  tile_image_url: null,
  tile_image_file_data: null,
  tile_tags: [],
  online_access_url: '',
});

const PLANS_QUERY_KEY = ['external_provider_plans'];
const TYPE_CONFIGS_QUERY_KEY = ['external_provider_type_configs'];

// ─── external_provider_plans ────────────────────────────────────────────────

const getExternalProviderPlans = async (): Promise<ExternalProviderPlan[]> => {
  return await axiosGet(`/external_provider_plans/`, {}, 'v3').then(
    (externalProviderPlansResponse) => externalProviderPlansResponse.data,
  );
};

const addExternalProviderPlanRequest = async (externalProviderPlan: ExternalProviderPlan) => {
  return await axiosPost(`/external_provider_plans/`, externalProviderPlan, 'v3').then(
    (externalProviderPlanResponse) => externalProviderPlanResponse.data,
  );
};

const updateExternalProviderPlanRequest = async (planId: number, externalProviderPlan: ExternalProviderPlan) => {
  return await axiosPut(`/external_provider_plans/${planId}/`, externalProviderPlan, 'v3').then(
    (externalProviderPlanResponse) => externalProviderPlanResponse.data,
  );
};

export const useExternalProviderPlans = () => {
  const { data, isLoading, error, refetch } = useQuery<ExternalProviderPlan[]>(
    PLANS_QUERY_KEY,
    getExternalProviderPlans,
  );

  return { data, isLoading, error, refetch };
};

export const useAddExternalProviderPlan = () => {
  const queryClient = useQueryClient();
  const addExternalProviderPlan = useMutation(
    (externalProviderPlan: ExternalProviderPlan) => addExternalProviderPlanRequest(externalProviderPlan),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PLANS_QUERY_KEY);
      },
      onError: (error) => {
        queryClient.invalidateQueries(PLANS_QUERY_KEY);
        console.error(error);
      },
      onMutate: (externalProviderPlan) => {
        queryClient.setQueryData(PLANS_QUERY_KEY, (oldData: any) => {
          if (oldData) {
            return [...oldData, externalProviderPlan];
          }
          return oldData;
        });
      },
    },
  );

  return { addExternalProviderPlan };
};

export const useUpdateExternalProviderPlan = (planId: number) => {
  const queryClient = useQueryClient();
  const updateExternalProviderPlan = useMutation(
    (externalProviderPlan: ExternalProviderPlan) => updateExternalProviderPlanRequest(planId, externalProviderPlan),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PLANS_QUERY_KEY);
      },
      onError: (error) => {
        queryClient.invalidateQueries(PLANS_QUERY_KEY);
        console.error(error);
      },
      onMutate: (externalProviderPlan) => {
        queryClient.setQueryData(PLANS_QUERY_KEY, (oldData: any) => {
          if (oldData) {
            return oldData.map((externalProviderPlanItem: ExternalProviderPlan) => {
              if (externalProviderPlanItem.id === externalProviderPlan.id) {
                return { ...externalProviderPlanItem, ...externalProviderPlan };
              }
              return externalProviderPlanItem;
            });
          }
          return oldData;
        });
      },
    },
  );

  return { updateExternalProviderPlan };
};

// ─── external_provider_type_configs ─────────────────────────────────────────
// Shared (provider, provider_type) content rows. Edited from the +Care
// Manage Content page; surfaced on each plan via the dump-only `type_configs`
// field above.

const getExternalProviderTypeConfigs = async (): Promise<ProviderTypeConfig[]> => {
  return await axiosGet(`/external_provider_type_configs/`, {}, 'v3').then((response) => response.data);
};

const createExternalProviderTypeConfigRequest = async (config: ProviderTypeConfig) => {
  const { id: _id, tile_image_url: _tileImageUrl, ...payload } = config;
  return await axiosPost(`/external_provider_type_configs/`, payload, 'v3').then((response) => response.data);
};

const updateExternalProviderTypeConfigRequest = async (configId: number, config: ProviderTypeConfig) => {
  // provider / provider_type are immutable identity fields on the backend.
  const {
    id: _id,
    tile_image_url: _tileImageUrl,
    provider: _provider,
    provider_type: _providerType,
    ...payload
  } = config;
  return await axiosPut(`/external_provider_type_configs/${configId}/`, payload, 'v3').then(
    (response) => response.data,
  );
};

export const useExternalProviderTypeConfigs = () => {
  const { data, isLoading, error, refetch } = useQuery<ProviderTypeConfig[]>(
    TYPE_CONFIGS_QUERY_KEY,
    getExternalProviderTypeConfigs,
  );
  return { data, isLoading, error, refetch };
};

export const useCreateExternalProviderTypeConfig = () => {
  const queryClient = useQueryClient();
  const createExternalProviderTypeConfig = useMutation(
    (config: ProviderTypeConfig) => createExternalProviderTypeConfigRequest(config),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(TYPE_CONFIGS_QUERY_KEY);
        queryClient.invalidateQueries(PLANS_QUERY_KEY);
      },
      onError: (error) => {
        queryClient.invalidateQueries(TYPE_CONFIGS_QUERY_KEY);
        console.error(error);
      },
    },
  );
  return { createExternalProviderTypeConfig };
};

export const useUpdateExternalProviderTypeConfig = () => {
  const queryClient = useQueryClient();
  const updateExternalProviderTypeConfig = useMutation(
    ({ configId, config }: { configId: number; config: ProviderTypeConfig }) =>
      updateExternalProviderTypeConfigRequest(configId, config),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(TYPE_CONFIGS_QUERY_KEY);
        queryClient.invalidateQueries(PLANS_QUERY_KEY);
      },
      onError: (error) => {
        queryClient.invalidateQueries(TYPE_CONFIGS_QUERY_KEY);
        console.error(error);
      },
    },
  );
  return { updateExternalProviderTypeConfig };
};
